import * as path from 'path';
import { ProjectConfig } from '../../types';

function resolveAppRoot(
  appPath: string | undefined,
  projectRoot: string,
): string {
  if (!appPath) {
    return projectRoot;
  }
  if (path.isAbsolute(appPath)) {
    return appPath;
  }
  return path.join(projectRoot, appPath);
}

export default function resolveConfigPaths({
  config,
  projectRoot,
}: {
  config: ProjectConfig;
  projectRoot: string;
}): ProjectConfig {
  const appRoot = resolveAppRoot(config.appPath, projectRoot);

  const transformIfExists = <T, R>(
    value: T | undefined,
    transformer: (v: T) => R,
  ): R | undefined => (value ? transformer(value) : undefined);
  const resolvePath = (filePath: string): string =>
    path.isAbsolute(filePath) ? filePath : path.join(projectRoot, filePath);

  const result = {
    ...config,
    appPath: appRoot,
    icon: resolvePath(config.icon),
  };

  if (config.extraContentFiles) {
    result.extraContentFiles = transformIfExists(
      config.extraContentFiles,
      (extraContentFiles) =>
        extraContentFiles.map((extraContentFile: { from: string }) => ({
          ...extraContentFile,
          from: resolvePath(extraContentFile.from),
        })),
    );
  }

  if (config.extraResources) {
    result.extraResources = transformIfExists(
      config.extraResources,
      (extraResources) =>
        extraResources.map((extraResource: { from: string }) => ({
          ...extraResource,
          from: resolvePath(extraResource.from),
        })),
    );
  }

  if (config.linux) {
    result.linux = { ...config.linux };

    if (config.linux.icon) {
      result.linux.icon = resolvePath(config.linux.icon);
    }
  }

  if (config.mac) {
    result.mac = { ...config.mac };

    if (config.mac.entitlements) {
      result.mac.entitlements = resolvePath(config.mac.entitlements);
    }

    if (config.mac.entitlementsInherit) {
      result.mac.entitlementsInherit = resolvePath(
        config.mac.entitlementsInherit,
      );
    }

    if (config.mac.requirements) {
      result.mac.requirements = resolvePath(config.mac.requirements);
    }

    if (config.mac.icon) {
      result.mac.icon = resolvePath(config.mac.icon);
    }
  }

  if (config.mas) {
    result.mas = { ...config.mas };

    if (config.mas.entitlements) {
      result.mas.entitlements = resolvePath(config.mas.entitlements);
    }

    if (config.mas.entitlementsInherit) {
      result.mas.entitlementsInherit = resolvePath(
        config.mas.entitlementsInherit,
      );
    }

    if (config.mas.provisioningProfile) {
      result.mas.provisioningProfile = resolvePath(
        config.mas.provisioningProfile,
      );
    }
  }

  if (config.dmg) {
    result.dmg = { ...config.dmg };

    if (config.dmg.background) {
      result.dmg.background = resolvePath(config.dmg.background);
    }
  }

  if (config.windows) {
    result.windows = { ...config.windows };

    if (config.windows.icon) {
      result.windows.icon = resolvePath(config.windows.icon);
    }
  }

  return result;
}
